/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ReactElement, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import type { IAppState } from '@/store/reducers'
import { useTranslation } from 'react-i18next'
import { Drawer, Input, Upload, Button, Space, List, message, Table, Empty, Skeleton, Spin, Typography, Tag } from 'antd'
import { PaperClipOutlined, DeleteOutlined, FileOutlined, SearchOutlined, DownloadOutlined } from '@ant-design/icons'
import type { UploadFile, UploadProps, RcFile } from 'antd/es/upload/interface'
import { IColProperty } from '@/pages/table2SearchAll'
import moment from 'moment'
import { ETechnologyContribution, ETechnologyLeadership } from '@/types'
import { downloadFile } from '@/utils/util'
import { IFileRes } from '@/store/models/process.model'
import { hideLoading } from '@/utils/util'
import { showLoading } from '@/utils/util'
import { getFile } from '@/services/progress.service'
import { postAISearch, uploadAISearchFile } from '@/services/progress.service'

export interface IProps {
    content?: string
    open: boolean
    // eslint-disable-next-line no-unused-vars
    setOpen: (open: boolean) => void
}

type EditableTableProps = Parameters<typeof Table>[0];
type ColumnTypes = Exclude<EditableTableProps['columns'], undefined>;

function AiSearchDrawer(props: IProps): ReactElement {
    const { open, setOpen } = props
    const { t, i18n } = useTranslation()
    const [inputValue, setInputValue] = useState<string>('')
    const [fileList, setFileList] = useState<UploadFile[]>([])
    const [loading, setLoading] = useState(false)
    const authInfo = useSelector((state: IAppState) => state.auth.authInfo)
    const login = useSelector((state: IAppState) => state.auth.userInfo?.login)
    const { Text } = Typography
    const [searchResults, setSearchResults] = useState<any[]>([])
    const technologicalLeadershipArr = useRef([
        {
            label: t('leadership'),
            id: ETechnologyLeadership.LEADING,
        },
        // {
        //     label: t('homeleadership'),
        //     id: ETechnologyLeadership.HOME_LEADING,
        // },
        {
            label: t('equalize'),
            id: ETechnologyLeadership.EQUAL,
        },
        {
            label: t('trail'),
            id: ETechnologyLeadership.TRAIL,
        },
    ])
    const technicalContributionArr = useRef([
        {
            label: t('innovationSellingPoint'),
            id: ETechnologyContribution.INNOVATION,
        },
        {
            label: t('keySellingPoint'),
            id: ETechnologyContribution.KEY,
        },
        {
            label: t('secondarySellingPoints'),
            id: ETechnologyContribution.SECONDARY,
        },
        {
            label: t('infrastructure'),
            id: ETechnologyContribution.BASIC,
        },
    ])
    const labelArr = [
        t('core') + (i18n.language !== 'zh' ? ' ' : '') + t('technology'),
        t('key') + (i18n.language !== 'zh' ? ' ' : '') + t('technology'),
        t('basicTechnology'),
        t('reserveTechnology'),
    ]

    const getUserLogin = (creator: string) => {
        const rs = creator.match(/[^(][a-zA-Z0-9]+(?=\))/g)
        return rs ? rs[0] : creator
    }

    const onClose = () => {
        setOpen(false)
        // 清空输入和文件
        setInputValue('')
        setFileList([])
        setSearchResults([])
    }

    // 处理文件上传
    const handleUpload: UploadProps['customRequest'] = async (options) => {
        const { file, onSuccess, onError } = options
        console.log(file, 'file')
        try {
            const formData = new FormData()
            formData.append('multipartFile', file as File)

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const data:any[] = await uploadAISearchFile(formData)
            console.log(data, 'data')

            if (data?.[0]) {
                const fileInfo:any = data[0]
                // 直接更新 fileList
                const newFile: UploadFile = {
                    uid: (file as RcFile).uid,
                    name: (file as RcFile).name,
                    status: 'done' as const,
                    url: fileInfo?.fullPath || '',
                    response: fileInfo
                }
                setFileList([newFile])
                onSuccess?.(fileInfo)
                message.success(`${newFile.name} ${t('1Z7Aqg6l06gYGD06O0IRc')}`)
            } else {
                onError?.(new Error(t('TQnIWI5GURIM98dEa4ReO')))
                message.error(t('TQnIWI5GURIM98dEa4ReO'))
            }
        } catch (error) {
            console.log(error, 'error')
            onError?.(error as Error)
            message.error(t('TQnIWI5GURIM98dEa4ReO'))
        }
    }

    // 文件变化处理
    const handleFileChange: UploadProps['onChange'] = (info) => {
        console.log(info, 'info')
        // 只处理上传失败的情况
        if (info.file.status === 'error') {
            message.error(`${info.file.name} ${t('TQnIWI5GURIM98dEa4ReO')}`)
        }
    }

    // 删除文件
    const handleRemoveFile = (file: UploadFile) => {
        const newFileList = fileList.filter(item => item.uid !== file.uid)
        setFileList(newFileList)
    }

    // 搜索功能
    const handleSearch = async () => {
        if (!inputValue.trim() && fileList.length === 0) {
            message.warning('请输入搜索内容或上传文件')
            return
        }

        setLoading(true)
        try {
            // 这里添加搜索逻辑
            console.log('搜索内容:', inputValue)
            console.log('附件:', fileList)

            // 模拟API调用和搜索结果
            await new Promise(resolve => setTimeout(resolve, 1000))

            // Create FormData and append file if exists
            // const formData = new FormData()
            // if (fileList[0] && fileList[0].originFileObj) {
            //     formData.append('file', fileList[0].originFileObj)
            // }

            const data:any[] = await postAISearch({
                keyWord: inputValue,
                filePath: fileList[0]?.url || '',
            })
            const tempData = data.map((item: any) => {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
                const { children, ...rest } = item
                return rest
            })
            console.log('搜索结果:', tempData)
            setSearchResults(tempData)
            message.success('搜索完成')
        } catch (error) {
            message.error('搜索失败')
        } finally {
            setLoading(false)
        }
    }

    // 处理回车搜索
    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleSearch()
        }
    }

    const findLabel = (arr: any[], id: number) =>
        arr?.find((item: any) => item.id === id)?.label ?? ''


    const viewAttach = (value: IFileRes[]) => {
        return value.length ? (
            <List
                itemLayout="horizontal"
                dataSource={value}
                renderItem={(item) => (
                    <List.Item
                        className="attach_name"
                        actions={
                            (authInfo && Reflect.has(authInfo, 'downloadAttach')) ||
                                getUserLogin(item.creator) === login
                                ? [
                                    <Text
                                        className="txt_blue hand"
                                        title={t('downloadFile')}
                                        onClick={async () => {
                                            showLoading(<Spin size="large" />)
                                            const res = await getFile(item.id).finally(() => {
                                                hideLoading()
                                            })
                                            downloadFile(res, item.fileName)
                                        }}
                                    >
                                        <DownloadOutlined style={{ fontSize: 16 }} />
                                    </Text>,

                                ]
                                : []
                        }
                    >
                        <Skeleton avatar title={false} loading={false} active>
                            <List.Item.Meta
                                title={
                                    <span className="word" title={item.fileName}>
                                        {item.fileName}
                                    </span>
                                }
                            />
                        </Skeleton>
                    </List.Item>
                )}
            />
        ) : (
            ''
        )
    }

    const openDetailTab = (record: any) => {
        window.open(`/relevantByFeedBackList?progressId=${record.id}&name=${record.proposalName}&type=${record.reviewType}`, '_blank')
    }

    const allColumns: (ColumnTypes[number] & IColProperty)[] =
        [
            {
                title: () => <span>{t('proposalName')}</span>,
                dataIndex: 'proposalName',
                key: 'proposalName',
                width: 400,
                ellipsis: { showTitle: true },
                fixed: 'left',
                render(value: string, record: any) {
                    if (Reflect.has(record, 'parent') && record.parent === null) {
                        return <span className='hand' style={{ color: '#036fc0' }} onClick={() => {
                            openDetailTab(record)
                        }}>{value}</span>
                    } else {
                        return (
                            <div className='hand' style={{ color: '#036fc0' }} onClick={() => {
                                openDetailTab(record)
                            }}>
                                <Tag color="error" style={{ marginRight: 4 }}>{t('secondReview')}</Tag>
                                <span>{value}</span>
                            </div>
                        )
                    }
                },

            },
            {
                title: () => <span>{t('creator')}</span>,
                dataIndex: 'creator',
                key: 'creator',
                width: 200,
                ellipsis: { showTitle: true },
            },
            {
                title: () => <span>{t('Work Group')}</span>,
                dataIndex: 'workGroup',
                key: 'workgroup',
                width: 200,
                ellipsis: { showTitle: true },
                render(value: any) {
                    return value?.workGroupName
                },

            },
            {
                title: () => (
                    <span>
                        {t('reviewMeeting') + (i18n.language !== 'zh' ? ' ' : '') + t('time')}
                    </span>
                ),
                dataIndex: 'reviewDate',
                key: 'reviewDate',
                width: 200,
                ellipsis: { showTitle: true },
                render: (value: Date, record: any) => {
                    return (
                        moment(value).format('YYYY-MM-DD') +
                        (record.reviewDateHours ? ' ' + record.reviewDateHours : '')
                    )
                },
            },
            {
                title: () => <span>{t('feedDueDate')}</span>,
                dataIndex: 'tmtReviewDueDate',
                key: 'tmtReviewDueDate',
                width: 150,
                ellipsis: { showTitle: true },
                render: (value: Date) => {
                    return moment(value).format('YYYY-MM-DD')
                },
            },
            {
                title: () => <span>{t('feedEndDate')}</span>,
                dataIndex: 'dueDate',
                key: 'dueDate',
                width: 150,
                ellipsis: { showTitle: true },
                render: (value: Date) => {
                    return moment(value).format('YYYY-MM-DD')
                },
            },
            {
                title: () => <span>{t('technologicalLeadership')}</span>,
                dataIndex: 'technologyLeadership',
                key: 'technologyLeadership',
                width: 150,
                ellipsis: { showTitle: true },
                render(value: number) {
                    return findLabel(technologicalLeadershipArr.current, value)
                },
            },
            {
                title: () => <span>{t('technicalContribution')}</span>,
                dataIndex: 'technologyContribution',
                key: 'technologyContribution',
                width: 150,
                ellipsis: { showTitle: true },
                render(value: number) {
                    return findLabel(technicalContributionArr.current, value)
                },

            },
            {
                title: () => <span>{t('techType')}</span>,
                dataIndex: 'coreTechnology',
                key: 'coreTechnology',
                width: 150,
                ellipsis: { showTitle: true },
                render(value: number) {
                    return labelArr[value]
                },

            },
            {
                title: () => <span>{t('attach')}</span>,
                dataIndex: 'files',
                key: 'files',
                width: 250,
                ellipsis: { showTitle: true },
                render: viewAttach,
            },
        ]


    // 封装拼接字符串的函数
    const joinTranslatedStrings = (...translations: string[]) => {
        return translations.join(i18n.language !== 'zh' ? ' ' : '')
    }

    return (
        <Drawer
            width={800}
            title={t('ai search')}
            placement="left"
            onClose={onClose}
            visible={open}
            bodyStyle={{ padding: '16px', display: 'flex', flexDirection: 'column', height: '100%' }}
        >
            {/* 搜索区域 */}
            <div style={{ marginBottom: '16px' }}>
                {/* 文件列表 */}
                {fileList.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                        <List
                            size="small"
                            dataSource={fileList}
                            renderItem={(file) => (
                                <List.Item
                                    style={{
                                        padding: '4px 8px',
                                        background: '#f6f6f6',
                                        borderRadius: '4px',
                                        marginBottom: '4px'
                                    }}
                                    actions={[
                                        <Button
                                            type="text"
                                            size="small"
                                            icon={<DeleteOutlined />}
                                            onClick={() => handleRemoveFile(file)}
                                            danger
                                        />
                                    ]}
                                >
                                    <List.Item.Meta
                                        avatar={<FileOutlined />}
                                        title={
                                            <span style={{ fontSize: '12px' }}>
                                                {file.name}
                                            </span>
                                        }
                                    />
                                </List.Item>
                            )}
                        />
                    </div>
                )}

                {/* 搜索框和按钮 */}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                        <Input
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder={joinTranslatedStrings(t('Please Input'), t('keyword'))}
                            prefix={<SearchOutlined />}
                        />
                    </div>

                    <Space>
                        <Upload
                            customRequest={handleUpload}
                            onChange={handleFileChange}
                            fileList={[]}
                            showUploadList={false}
                            accept=".pdf,.doc,.docx,.ppt,.pptx,.txt,.md,.log"
                            beforeUpload={(file) => {
                                const isLt10M = file.size / 1024 / 1024 < 10
                                if (!isLt10M) {
                                    message.error(t('fileSizeLimit'))
                                    return false
                                }
                                return true
                            }}
                        >
                            <Button
                                icon={<PaperClipOutlined />}
                                title={t('upload attachment')}
                            />
                        </Upload>

                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleSearch}
                            loading={loading}
                        >
                            {t('search')}
                        </Button>
                    </Space>
                </div>

                {/* 提示信息 */}
                <div style={{
                    fontSize: '12px',
                    color: '#999',
                    marginTop: '8px'
                }}>
                    {t('ai search description')}
                </div>
            </div>

            {/* 搜索结果表格 */}
            <div style={{ flex: 1, overflow: 'auto' }}>
                {searchResults.length > 0 ? (
                    <Table
                        columns={allColumns}
                        dataSource={searchResults}
                        pagination={false}
                        scroll={{ y: 400 }}
                        size="small"
                    />
                ) : (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '300px',
                        background: '#fafafa',
                        borderRadius: '8px'
                    }}>
                        <Empty
                            description={joinTranslatedStrings(t('no search results'), t('Please Input'), t('keyword'))}
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                    </div>
                )}
            </div>
        </Drawer>
    )
}

export default AiSearchDrawer
